import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useKiosk } from '../../contexts/KioskContext'
import { useAuth } from '../../contexts/AuthContext'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import Card from '../ui/Card'
import Button from '../ui/Button'
import Badge from '../ui/Badge'
import KioskModeToggle from '../kiosk/KioskModeToggle'
import RewardRequestNotifications from '../rewards/RewardRequestNotifications'

const { FiTablet, FiClock, FiUsers, FiMessageSquare, FiAlertCircle } = FiIcons

const KioskSettings = () => {
  const { family } = useAuth()
  const { kioskSession, isKioskMode } = useKiosk()

  const getSessionDuration = () => {
    if (!kioskSession.startTime) return '0 minutes'
    const now = new Date()
    const start = new Date(kioskSession.startTime)
    const minutes = Math.floor((now - start) / (1000 * 60))
    
    if (minutes < 60) {
      return `${minutes} minute${minutes !== 1 ? 's' : ''}`
    }
    
    const hours = Math.floor(minutes / 60)
    const remainingMinutes = minutes % 60
    return `${hours}h ${remainingMinutes}m`
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <div className="flex items-center gap-3 mb-4">
          <SafeIcon icon={FiTablet} className="text-primary-500 text-xl" />
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Kiosk Mode Settings</h2>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Configure and monitor your family's kiosk mode experience
            </p>
          </div>
        </div>
      </Card>

      {/* Kiosk Status */}
      {isKioskMode && (
        <Card className="bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 border-green-200 dark:border-green-800">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-green-800 dark:text-green-300 mb-1">
                🟢 Kiosk Mode Active
              </h3>
              <div className="text-sm text-green-700 dark:text-green-400 space-y-1">
                <div>Session Duration: {getSessionDuration()}</div>
                <div>Current Child: {kioskSession.selectedChild 
                  ? family?.children?.find(c => c.id === kioskSession.selectedChild)?.name || 'Unknown'
                  : 'Child Selection Screen'}</div>
                <div>Last Activity: {kioskSession.lastActivity 
                  ? new Date(kioskSession.lastActivity).toLocaleTimeString() 
                  : 'N/A'}</div>
              </div>
            </div>
            <Badge variant="success" className="text-sm">
              Active
            </Badge>
          </div>
        </Card>
      )}

      {/* Main Kiosk Toggle and Settings */}
      <KioskModeToggle />

      {/* Reward Requests */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <SafeIcon icon={FiMessageSquare} className="text-purple-500" />
            Reward Requests
          </h3>
        </div>

        <RewardRequestNotifications showAll={true} />
      </Card>

      {/* Usage Statistics */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
          <SafeIcon icon={FiClock} className="text-blue-500" />
          Usage Statistics
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
              {isKioskMode ? '1' : '0'}
            </div>
            <div className="text-sm text-blue-700 dark:text-blue-300">Active Sessions</div>
          </div>
          <div className="text-center p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
            <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
              {family?.children?.length || 0}
            </div>
            <div className="text-sm text-purple-700 dark:text-purple-300">Available Children</div>
          </div>
        </div>
      </Card>

      {/* Security Notice */}
      <Card className="bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800">
        <div className="flex items-start gap-3">
          <SafeIcon icon={FiAlertCircle} className="text-yellow-600 dark:text-yellow-400 text-lg mt-1" />
          <div>
            <h4 className="font-medium text-yellow-800 dark:text-yellow-200 mb-1">Security Notice</h4>
            <div className="text-sm text-yellow-700 dark:text-yellow-300 space-y-1">
              <p>• Keep your parent PIN secure and don't share it with children</p>
              <p>• Kiosk mode restricts access to parent areas and settings</p>
              <p>• Children can only complete tasks and request rewards</p>
              <p>• Sessions automatically timeout after the configured period</p>
              <p>• All activity is logged for your review</p>
            </div>
          </div>
        </div>
      </Card>

      {/* Tips for Success */}
      <Card className="bg-gradient-to-r from-primary-50 to-secondary-50 dark:from-primary-900/20 dark:to-secondary-900/20">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
          💡 Tips for Successful Kiosk Mode
        </h3>
        <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
          <div className="flex items-start gap-2">
            <span className="font-bold text-primary-600 dark:text-primary-400">•</span>
            <span>Set up a dedicated device or browser for kiosk mode</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="font-bold text-primary-600 dark:text-primary-400">•</span>
            <span>Create clear, simple tasks that children can understand independently</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="font-bold text-primary-600 dark:text-primary-400">•</span>
            <span>Set reasonable reward thresholds to keep children motivated</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="font-bold text-primary-600 dark:text-primary-400">•</span>
            <span>Check reward requests regularly to maintain engagement</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="font-bold text-primary-600 dark:text-primary-400">•</span>
            <span>Use auto-logout to prevent unauthorized extended use</span>
          </div>
        </div>
      </Card>
    </div>
  )
}

export default KioskSettings